import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { historyForProduct } from "@/lib/priceHistory";
import { cancelLocalAlert, checkLocalAlert, localAlertFor, saveLocalAlert } from "@/lib/priceAlerts";

function roundMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  const digits = ["JPY", "INR"].includes((currency || "").toUpperCase()) ? 0 : 2;
  const scale = 10 ** digits;
  return Math.round(n * scale) / scale;
}

function currencySymbol(code) {
  try {
    const part = new Intl.NumberFormat("en", {
      style: "currency",
      currency: code || "USD",
      currencyDisplay: "narrowSymbol",
    }).formatToParts(1).find((p) => p.type === "currency");
    return part?.value || code || "$";
  } catch {
    return code || "$";
  }
}

export function distinctStorePrices(offers, product) {
  const rows = [];
  const seen = new Set();
  for (const o of offers || []) {
    const price = Number(o?.price);
    if (!Number.isFinite(price) || price <= 0) continue;
    const store = String(o.seller_name || o.source || "store").toLowerCase().trim();
    const key = `${store}|${price.toFixed(2)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ store, price, seller: o.seller_name || o.source || "Store" });
  }
  if (!rows.length) {
    const price = Number(product?.price);
    if (Number.isFinite(price) && price > 0) {
      rows.push({ store: "listing", price, seller: product.seller_name || "This listing" });
    }
  }
  return rows;
}

export default function BuyRecommendation({ product, offers, formatMoney, history }) {
  const { user } = useAuth();
  const locale = useLocale() || {};
  const navigate = useNavigate();
  const rows = distinctStorePrices(offers, product);
  const hist = historyForProduct(product, history);
  const current = Number(product?.price) > 0 ? Number(product.price) : hist.current;
  const listed = (product?.currency || hist.currency || "USD").toUpperCase();
  const shown = (locale.currency || listed).toUpperCase();
  const convert = locale.convert || ((amount) => amount);
  const convertBack = locale.convertBack || ((amount) => amount);
  const currency = listed;
  const [target, setTarget] = useState("");
  const [touched, setTouched] = useState(false);
  const [alertRow, setAlertRow] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTouched(false);
  }, [product?.id]);

  useEffect(() => {
    if (touched || !(current > 0)) return;
    setTarget(roundMoney(convert(current, listed), shown));
  }, [product?.id, current, listed, shown, touched, convert]);

  useEffect(() => {
    const id = product?.id;
    if (!id) return;
    const before = localAlertFor(id);
    const local = checkLocalAlert(id, current) || before;
    if (local && local.status !== "cancelled") setAlertRow(local);
    if (before?.status === "watching" && local?.status === "triggered") {
      toast.success(`Price dropped to ${formatMoney(local.triggered_price, local.currency || currency)}`);
    }
    if (!user) return;
    api.get(`/price-alerts/for/${encodeURIComponent(id)}`)
      .then(({ data }) => {
        if (data?.alert) setAlertRow(data.alert);
      })
      .catch(() => {});
  }, [product?.id, user, current, currency, formatMoney]);

  if (!rows.length && !hist.current) return null;

  const storeVals = rows.map((r) => r.price);
  const uniqueStores = new Set(storeVals.map((n) => n.toFixed(2))).size;
  const liveCompare = rows.length >= 2 && uniqueStores >= 2;
  const enough = hist.enough || liveCompare;
  const lowest = hist.enough ? hist.lowest : liveCompare ? Math.min(...storeVals) : hist.lowest;
  const highest = hist.enough ? hist.highest : liveCompare ? Math.max(...storeVals) : hist.highest;
  const average = hist.enough
    ? hist.average
    : liveCompare
      ? storeVals.reduce((a, b) => a + b, 0) / storeVals.length
      : hist.average;
  const span = (highest ?? 0) - (lowest ?? 0) || 1;
  const cheapScore = enough && highest != null && current != null
    ? Math.max(0, Math.min(100, ((highest - current) / span) * 100))
    : hist.cheap_score;
  const rec = hist.enough
    ? hist.label
    : liveCompare
      ? (current <= lowest * 1.08
        ? "Go ahead & buy now"
        : current >= highest * 0.9
          ? "Wait for a better price"
          : "Fair price — compare stores")
      : hist.label;
  const good = rec === "Go ahead & buy now";
  const wait = rec === "Wait for a better price";

  const setAlert = async () => {
    if (!user) return navigate("/auth?mode=login");
    const typed = Number(target);
    if (!Number.isFinite(typed) || typed <= 0) {
      toast.error("Enter a target price");
      return;
    }
    const amount = touched ? roundMoney(convertBack(typed, listed), listed) : current;
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a target price");
      return;
    }
    setSaving(true);
    const local = saveLocalAlert({
      listing_id: product.id,
      target_price: amount,
      currency: listed,
      title: product.title,
      user_id: user.id,
    });
    setAlertRow(local);
    try {
      const { data } = await api.post("/price-alerts", {
        product_id: product.id,
        target_price: amount,
        currency: listed,
        title: product.title || "",
      });
      if (data?.alert) setAlertRow({ ...data.alert, email_ready: data.email_ready, email: data.email, emailed: data.emailed });
      if (data?.emailed) {
        toast.success(`Watching. Confirmation emailed to ${data.email}.`);
      } else if (data?.email_error) {
        toast.error(`Watching saved, but email failed: ${data.email_error}`);
      } else if (data?.email_ready === false) {
        toast.success(`Watching below ${formatMoney(amount, currency)}. Add a mailer in admin to email ${data.email || "you"}.`);
      } else {
        toast.success(`Watching. We’ll email ${data?.email || "you"} if this falls below ${formatMoney(amount, currency)}.`);
      }
    } catch (e) {
      toast.success(`Watching on this device below ${formatMoney(amount, currency)}. Sign-in sync needs the latest API.`);
      if (e?.response?.status && e.response.status !== 404) toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const clearAlert = async () => {
    const id = product?.id;
    cancelLocalAlert(id);
    setAlertRow(null);
    if (alertRow?.id && user && !String(alertRow.id).startsWith("local:")) {
      try {
        await api.delete(`/price-alerts/${alertRow.id}`);
      } catch {
        /* local cancel still stands */
      }
    }
    toast.success("Price alert removed");
  };

  const alertLabel = !user
    ? "Login & set price alert"
    : alertRow?.status === "watching"
      ? "Update alert"
      : "Set price alert";

  return (
    <>
    <aside className="h-full border border-aura-border bg-aura-surface rounded-lg p-4 flex flex-col" data-testid="buy-recommendation">
      <h2 className="text-[15px] font-semibold">Should you buy now?</h2>
      <p className="text-[11px] text-aura-muted mt-0.5">
        {hist.enough
          ? `${hist.days} day${hist.days === 1 ? "" : "s"} of this item’s price`
          : liveCompare
            ? `${rows.length} store prices right now`
            : "Price history is still filling in"}
      </p>

      <div className="mt-3 flex items-center gap-3">
        <div className="shrink-0 w-[108px]">
          <div className={`relative h-14 overflow-hidden ${enough ? "" : "opacity-40"}`}>
            <div
              className="absolute inset-0 rounded-t-full"
              style={{
                background: "conic-gradient(from 180deg, #ef4444 0deg, #eab308 90deg, #22c55e 180deg)",
                maskImage: "radial-gradient(circle at 50% 100%, transparent 58%, #000 59%)",
                WebkitMaskImage: "radial-gradient(circle at 50% 100%, transparent 58%, #000 59%)",
              }}
            />
            {enough && (
              <div
                className="absolute bottom-0 left-1/2 origin-bottom h-[52px] w-0.5 bg-aura-ink"
                style={{ transform: `translateX(-50%) rotate(${-90 + (cheapScore / 100) * 180}deg)` }}
              />
            )}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-aura-ink" />
          </div>
          <div className="flex justify-between text-[9px] text-aura-muted">
            <span>Bad</span>
            <span>Good</span>
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-aura-muted">Our recommendation</div>
          <div className={`mt-0.5 text-[14px] font-semibold leading-snug ${good ? "text-emerald-400" : wait ? "text-amber-300" : "text-aura-ink"}`}>
            {rec}
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4">
        <div className="text-[10px] uppercase tracking-wide text-aura-muted">Price stats</div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-center">
          <div className="border border-aura-border rounded-md px-2 py-2">
            <div className="text-[10px] text-aura-muted">Highest</div>
            <div className="font-mono text-[13px] mt-0.5">{highest != null ? formatMoney(highest, currency) : "—"}</div>
          </div>
          <div className="border border-aura-border rounded-md px-2 py-2">
            <div className="text-[10px] text-aura-muted">Average</div>
            <div className="font-mono text-[13px] mt-0.5">{average != null ? formatMoney(average, currency) : "—"}</div>
          </div>
          <div className="col-span-2 border border-aura-border rounded-md px-2 py-2">
            <div className="text-[10px] text-aura-muted">Lowest</div>
            <div className="font-mono text-[13px] mt-0.5">{lowest != null ? formatMoney(lowest, currency) : "—"}</div>
          </div>
        </div>
      </div>
    </aside>
    <div className="lg:col-span-3">
      <h3 className="text-center text-[15px] font-semibold mb-3">Set price drop alert to buy later</h3>
      <div className="mx-auto flex max-w-xl items-center gap-2 rounded-lg border border-aura-border bg-aura-surface px-3 py-2">
        <Bell className="h-4 w-4 shrink-0 text-aura-muted" />
        <span className="font-mono text-[16px] text-aura-muted" data-testid="price-alert-currency">{currencySymbol(shown)}</span>
        <input
          type="number"
          min="0"
          step={shown === "INR" || shown === "JPY" ? "1" : "0.01"}
          value={target}
          onChange={(e) => { setTouched(true); setTarget(e.target.value); }}
          data-testid="price-alert-target"
          className="min-w-0 flex-1 bg-transparent font-mono text-[16px] outline-none"
        />
        <button
          type="button"
          onClick={setAlert}
          disabled={saving}
          data-testid="price-alert-btn"
          className="shrink-0 rounded-md btn-accent px-4 py-2.5 text-[13px] font-semibold disabled:opacity-60"
        >
          {alertLabel}
        </button>
      </div>
      {alertRow?.status === "triggered" && (
        <p className="mt-2 text-center text-[12px]" data-testid="price-alert-triggered">
          Dropped to {formatMoney(alertRow.triggered_price, alertRow.currency || currency)}.{" "}
          <Link to="/profile" className="link-underline text-aura-accent">See your alerts</Link>
        </p>
      )}
      {alertRow?.status === "watching" && (
        <p className="mt-2 text-center text-[12px] text-aura-muted" data-testid="price-alert-watching">
          Watching below {formatMoney(alertRow.target_price, alertRow.currency || currency)}.{" "}
          <button type="button" onClick={setAlert} disabled={saving} className="link-underline disabled:opacity-50" data-testid="price-alert-resend">
            Resend email
          </button>
          {" · "}
          <button type="button" onClick={clearAlert} className="link-underline" data-testid="price-alert-remove">
            Remove
          </button>
        </p>
      )}
    </div>
    </>
  );
}
